import React, {
  createContext,
  useCallback,
  useEffect,
  useRef,
  useState,
} from "react";
import { useQueryClient } from "@tanstack/react-query";
import { getLoginToken, getStoredUser, setStoredUser } from "../storage";
import { getDecodedJWT, isAuthenticated } from "../utils";
import { useAuthenticatedUser } from "./hooks";
import { ChildProps, userProps } from "../interface";
import SessionTimeoutModal from "../modules/Timeout";

export interface AuthContextType {
  user?: userProps;
  token?: string;
  isAuthenticated: boolean;
  authenticate(token: string): void;
  logout(): void;
  updateUser(data: userProps): void;
}

export const AuthContext = createContext<AuthContextType>({
  user: undefined,
  token: undefined,
  isAuthenticated: false,
  authenticate: () => {}, // no-op
  logout: () => {}, // no-op
  updateUser: () => {}, // no-op
});

const TIMEOUT_WARNING_SECONDS = 60;

function AuthContextProvider({ children }: ChildProps) {
  const [authToken, setAuthToken] = useState<string | undefined>();
  const [user, setUser] = useState<userProps | undefined>();
  const userDetails = useAuthenticatedUser();
  const queryClient = useQueryClient();
  const [showTimeoutModal, setShowTimeoutModal] = useState(false);
  const logoutTriggered = useRef(false);

  const timeoutTimerRef = useRef<number>();
  const warningTimerRef = useRef<number>();

  // On mount, load token & user from storage
  useEffect(() => {
    const token = getLoginToken();
    if (token) setAuthToken(token);
  }, []);

  useEffect(() => {
    const stored = getStoredUser();
    if (stored) setUser(stored);
  }, []);

  // Sync in case useAuthenticatedUser hook returns fresh data
  useEffect(() => {
    if (userDetails) {
      setUser(userDetails);
    }
  }, [userDetails]);

  const clearAllTimers = useCallback(() => {
    if (timeoutTimerRef.current) {
      window.clearTimeout(timeoutTimerRef.current);
    }
    if (warningTimerRef.current) {
      window.clearTimeout(warningTimerRef.current);
    }
  }, []);

  const logout = useCallback(() => {
    if (logoutTriggered.current) return;
    logoutTriggered.current = true;
    localStorage.clear();
    setAuthToken(undefined);
    setUser(undefined);
    queryClient.clear();
  }, [queryClient]);

  // If the token has expired or is missing, force logout
  useEffect(() => {
    if (!isAuthenticated()) {
      logout();
    }
  }, [logout]);

  useEffect(() => {
    if (!isAuthenticated()) {
      clearAllTimers();
      return;
    }

    clearAllTimers();

    const decoded = getDecodedJWT();
    const now = Date.now() / 1000;
    const expiresIn = (decoded?.exp ?? now) - now;

    if (expiresIn <= 0) {
      logout();
      return;
    }

    const warningTime = expiresIn - TIMEOUT_WARNING_SECONDS;

    if (warningTime > 0) {
      warningTimerRef.current = window.setTimeout(() => {
        setShowTimeoutModal(true);
      }, warningTime * 1000);
    } else {
      setShowTimeoutModal(true);
    }

    timeoutTimerRef.current = window.setTimeout(() => {
      logout();
    }, expiresIn * 1000);

    return () => {
      clearAllTimers();
    };
  }, [logout, clearAllTimers, user]);

  function updateUser(data: userProps) {
    setUser(data);
    setStoredUser(data);
  }

  function authenticate(token: string) {
    setAuthToken(token);

    // decode & shape user
    const decoded: any = getDecodedJWT();

    const u: userProps = {
      _id: decoded._id || "",
      firstname: decoded.FirstName || "",
      lastname: decoded.LastName || "",
      middlename: "",
      fullname: "",
      phone: decoded.PhoneNumber || "",
      email: decoded.email || "",
      role: decoded.role || "",
      isVerified: decoded.IsVerified || false,
    };

    setUser(u);
    setStoredUser(u);
  }

  const value: AuthContextType = {
    user,
    token: authToken,
    isAuthenticated: Boolean(authToken),
    authenticate,
    logout,
    updateUser,
  };

  const handleModalLogout = () => {
    setShowTimeoutModal(false);
    logout();
  };

  const handleExtend = () => {
    setShowTimeoutModal(false);
    clearAllTimers();
  };

  return (
    <AuthContext.Provider value={value}>
      {children}

      <SessionTimeoutModal
        visible={showTimeoutModal}
        secondsLeft={TIMEOUT_WARNING_SECONDS}
        onExtend={handleExtend}
        onLogout={handleModalLogout}
      />
    </AuthContext.Provider>
  );
}

export default AuthContextProvider;
